import axios from 'axios'
import { computed, ComputedRef, reactive } from 'vue'

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import WorldTrafficStats from '../DTOs/WorldTrafficStats'
import { ValidWorldMapTypes } from '../Views/WorldTrafficChart/WorldTraffic'
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from './types'

/**
 * Represents the resonse from the server. Eventually this type will be auto generated
 * by the backend but for now its here
 */
export type WorldTrafficResponseItem = {
  name: string
  value: number
}
export type WorldTrafficResponseData = {
  dataset: WorldTrafficResponseItem[]
  total: number
}

export type WorldTrafficExtraArgs = { map?: ValidWorldMapTypes }

export type iWorldTrafficAPI = {
  dataset: ComputedRef<Readonly<WorldTrafficStats>>
  loading: ComputedRef<boolean>
  setRouter: (router: HttpRouter<'get'>) => iWorldTrafficAPI
  fetchForTeam: (args: FetchForTeamArgs<WorldTrafficExtraArgs>) => Promise<void>
  fetchForAlias: (args: FetchForAliasArgs<WorldTrafficExtraArgs>) => Promise<void>
  fetchForTag: (args: FetchForTagArgs<WorldTrafficExtraArgs>) => Promise<void>
  fetchForMap: (map: ValidWorldMapTypes) => Promise<void>
}

/**
 * Returns data that can be used as a sensible default for testing and development
 */
export function DefaultWorldStats(): WorldTrafficResponseItem[] {
  return [
    {
      name: 'mx',
      value: 36,
    },
    {
      name: 'pt',
      value: 6,
    },
    {
      name: 'us',
      value: 1,
    },
  ]
}

/**
 * A tool to allow you to fetch and interact with "popular times" analytics
 * data fetched from the server.
 */
export default function WorldTrafficAPI(debug = false): iWorldTrafficAPI {
  const state = reactive<{
    dataset: WorldTrafficStats
    loading: boolean
  }>({
    dataset: new WorldTrafficStats(),
    loading: false,
  })

  let router: HttpRouter<'get'> = axios

  /**
   * Captures the most recent fetch call as a closure so it can be replayed against a
   * different map (drill-down / back-to-world) without re-passing the team/alias/tag context.
   */
  let lastFetch: ((map: ValidWorldMapTypes) => Promise<void>) | null = null

  const debugFakeData = {
    dataset: [
      { name: 'mx', value: 77 },
      { name: 'pt', value: 12 },
      { name: 'us', value: 4 },
      { name: 'ab', value: 9 },
      { name: 'cd', value: 9 },
      { name: 'cn', value: 9 },
      { name: 'ua', value: 9 },
      { name: 'ru', value: 9 },
      { name: 'in', value: 9 },
      { name: 'au', value: 9 },
      { name: 'ca', value: 9 },
      { name: 'gg', value: 9 },
    ],
    total: 174,
  }

  async function _fetch(url: string, map: ValidWorldMapTypes): Promise<void> {
    state.loading = true

    try {
      if (debug) {
        state.dataset = new WorldTrafficStats(
          debugFakeData.dataset,
          debugFakeData.total,
          map
        )
      } else {
        const response: HttpResponse<WorldTrafficResponseData> =
          await router.get(url)

        if (response.status !== 200) {
          throw new Error('Could not fetch world traffic stats')
        }

        state.dataset = new WorldTrafficStats(
          response.data.dataset,
          response.data.total,
          map,
        )
      }
    } finally {
      state.loading = false
    }
  }

  return {
    loading: computed(() => state.loading),
    dataset: computed(() => state.dataset),

    fetchForTeam: async function ({
      orgID,
      teamID = null,
      from,
      to,
      map = 'world',
      archived = false,
    }): Promise<void> {
      lastFetch = (m: ValidWorldMapTypes) =>
        _fetch(`/app/api/organization/${orgID}/team/${teamID}/analytics/location/${encodeURIComponent(m)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`, m)
      return lastFetch(map)
    },

    fetchForAlias: async function ({
      orgID,
      teamID = null,
      alias,
      from,
      to,
      map = 'world',
      archived = false,
    }): Promise<void> {
      lastFetch = (m: ValidWorldMapTypes) =>
        _fetch(`/app/api/organization/${orgID}/team/${teamID}/analytics/location/${encodeURIComponent(m)}?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`, m)
      return lastFetch(map)
    },

    fetchForTag: async function ({
      orgID,
      teamID = null,
      tag,
      from,
      to,
      map = 'world',
      archived = false,
    }): Promise<void> {
      lastFetch = (m: ValidWorldMapTypes) =>
        _fetch(`/app/api/organization/${orgID}/team/${teamID}/analytics/location/${encodeURIComponent(m)}?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`, m)
      return lastFetch(map)
    },

    /**
     * Re-fetches the most recently requested dataset against a different map. Used to
     * drill down into a country and to return to the world view.
     */
    fetchForMap: async function (map: ValidWorldMapTypes): Promise<void> {
      if (!lastFetch) {
        return
      }
      return lastFetch(map)
    },

    /**
     * Allows you to swap out the router for a fake one when writing tests
     */
    setRouter(newRouter) {
      router = newRouter
      return this
    },
  }
}
